import { createContext, useContext, useState, useEffect } from "react";

const FavoritesContext = createContext();

// Available sizes for perfumes
export const PERFUME_SIZES = [
  { id: "small", label: "30ml", shortLabel: "30" },
  { id: "medium", label: "60ml", shortLabel: "60" },
  { id: "large", label: "100ml", shortLabel: "100" },
];

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => {
    // Load from sessionStorage on init
    try {
      const saved = sessionStorage.getItem("sapphire_favorites");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save to sessionStorage when favorites change
  useEffect(() => {
    sessionStorage.setItem("sapphire_favorites", JSON.stringify(favorites));
  }, [favorites]);

  const addFavorite = (perfume) => {
    setFavorites((prev) => {
      // Check if already exists
      if (prev.some((f) => f.id === perfume.id)) {
        return prev;
      }
      // Add with default size (medium)
      return [...prev, { ...perfume, selectedSize: "medium" }];
    });
  };

  const removeFavorite = (perfumeId) => {
    setFavorites((prev) => prev.filter((f) => f.id !== perfumeId));
  };

  const toggleFavorite = (perfume) => {
    if (isFavorite(perfume.id)) {
      removeFavorite(perfume.id);
    } else {
      addFavorite(perfume);
    }
  };

  const isFavorite = (perfumeId) => {
    return favorites.some((f) => f.id === perfumeId);
  };

  const updateSize = (perfumeId, sizeId) => {
    setFavorites((prev) =>
      prev.map((f) =>
        f.id === perfumeId ? { ...f, selectedSize: sizeId } : f
      )
    );
  };

  const removeBulk = (ids) => {
    const idSet = new Set(ids);
    setFavorites((prev) => prev.filter((f) => !idSet.has(f.id)));
  };

  const clearFavorites = () => {
    setFavorites([]);
  };

  const getFavoritesCount = () => favorites.length;

  // Generate WhatsApp message for order
  const generateOrderMessage = () => {
    if (favorites.length === 0) return "";

    const header = "¡Hola! Me gustaría hacer el siguiente pedido:\n\n";
    
    const items = favorites.map((f, index) => {
      const size = PERFUME_SIZES.find((s) => s.id === f.selectedSize);
      return `${index + 1}. ${f.nombre} - ${f.marca} (${size?.label || "60ml"})`;
    }).join("\n");

    const footer = "\n\n¡Gracias!";

    return header + items + footer;
  };

  const getWhatsAppOrderLink = () => {
    const message = encodeURIComponent(generateOrderMessage());
    return `https://wa.me/525547630396?text=${message}`;
  };

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        addFavorite,
        removeFavorite,
        removeBulk,
        toggleFavorite,
        isFavorite,
        updateSize,
        clearFavorites,
        getFavoritesCount,
        generateOrderMessage,
        getWhatsAppOrderLink,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
};
